import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import {
  Breadcrumb,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderNextNavButton,
  HeaderNav,
  HeaderSidebarTrigger,
  HeaderPrevNavButton,
  useHeaderNav,
} from '@/app/ui/components/header';
import { EvaluationTypeLabelsEnum, AmbiguityTypes, queryKeys } from '@/app/utils/constants';
import { ContractsIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import { MainScrollArea } from '@/app/ui/components/main';
import { Spinner } from '@/app/ui/components/spinner';
import { useGetAmbiguities } from '@/app/components/features/evaluations/analysis/Ambiguities/useAmbiguity';
import { AmbiguityDetail } from '@/app/components/features/evaluations/analysis/Ambiguities/AmbiguityDetail';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getContractDetailsQuery } from '@/app/hooks/queries/useContracts';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { NotFound } from '@/app/ui/components/not-found';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/contracts/$contractId/_layout/(main)/ambiguities/$ambiguityId',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.contractHome');
  const tFeature = useTranslations('features.ambiguities');
  const { contractId, projectId, ambiguityId } = Route.useParams();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const { data: evaluation } = useSuspenseQuery(getContractDetailsQuery(contractId));

  const { data: allAmbiguitiesData, isLoading } = useGetAmbiguities({
    ambiguityType: AmbiguityTypes.CONTRACT,
    id: contractId,
  });
  const allStatements = allAmbiguitiesData?.statements;

  const data = useMemo(
    () => allStatements?.find((item) => item.id === ambiguityId),
    [allStatements, ambiguityId],
  );

  const { handlePrevious, handleNext, isPreviousDisabled, isNextDisabled, currentIndex } =
    useHeaderNav(allStatements || [], ambiguityId, (ambiguityId) => ({
      to: '/project/$projectId/contracts/$contractId/ambiguities/$ambiguityId',
      params: { projectId, contractId, ambiguityId },
    }));

  if (!data && !isLoading) return <NotFound />;

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/contracts/$contractId/ambiguities"
          params={{ projectId, contractId }}
        />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem
                to="/project/$projectId/contracts/$contractId"
                params={{ projectId, contractId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.CONTRACT} />
                {evaluation.name}
              </BreadcrumbMenuItem>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem className="md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/contracts/$contractId"
                params={{ projectId, contractId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.CONTRACT} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="md:hidden" />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/contracts/$contractId"
                params={{ projectId, contractId }}
              >
                <HugeiconsIcon icon={ContractsIcon} />
                {t('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/contracts/$contractId/ambiguities"
                params={{ projectId, contractId }}
              >
                {tFeature('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="tabular-nums">#{currentIndex + 1}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderNav>
          <HeaderPrevNavButton onClick={handlePrevious} disabled={isPreviousDisabled} />
          <HeaderNextNavButton onClick={handleNext} disabled={isNextDisabled} />
        </HeaderNav>
        <HeaderSidechatButton />
      </Header>
      <MainScrollArea>
        {isLoading ? (
          <Spinner kind="inset" />
        ) : data ? (
          <AmbiguityDetail
            data={data}
            currentIndex={currentIndex}
            ambiguityType={AmbiguityTypes.CONTRACT}
            evaluationId={contractId}
            queryKeysToInvalidate={[
              [queryKeys.AMBIGUITY_STATEMENTS, AmbiguityTypes.CONTRACT, contractId],
            ]}
          />
        ) : null}
      </MainScrollArea>
    </>
  );
}
